import { jsPDF } from "jspdf";
import { apiFetch } from "./api";
import { dateIST } from "@emr/shared/india";

/**
 * Save an app-generated jsPDF document to R2 via the /documents/r2-upload binding path.
 *
 * R2 key layout (since 2026-09-24 — adds patient/visit context for folder-based
 * retrieval; previously the category folder mixed all patients together):
 *   certificates/{patientId}/{YYYY-MM}/{uuid}-{name}.pdf
 *   referrals/{patientId}/{YYYY-MM}/{uuid}-{name}.pdf
 *   discharge-summaries/{patientId}/{YYYY-MM}/{uuid}-{name}.pdf (2026-10-16)
 *
 * 2026-10-13: the "prescriptions" category was removed — prescriptions are no
 * longer saved to R2; they are rebuilt from the DB on demand (print / patient
 * detail / reports archive all regenerate the PDF from medicines rows).
 *
 * patientId is expected (every referral is tied to a patient; certificates
 * can have a null patientId in edge cases). When patientId is missing the
 * segment falls back to `unsorted` so the key still validates against the
 * /documents/r2-upload prefix allowlist. base64-encodes the blob and POSTs to
 * /documents/r2-upload. Returns the r2Key.
 */
export async function savePdfToR2(
  doc: jsPDF,
  category: "certificates" | "referrals" | "discharge-summaries",
  fileName: string,
  patientId: string | undefined | null,
  visitId?: string,
  existingKey?: string,
): Promise<string> {
  // Replace-in-place (2026-10-16, discharge summaries): when existingKey is
  // given, the PDF is written to that exact R2 key — R2 `put` overwrites the
  // object atomically, so a re-save replaces the old PDF instead of minting a
  // new key and orphaning the old object. The key must still pass the
  // /documents/r2-upload prefix allowlist (validated server-side).
  if (existingKey) {
    const blob = doc.output("blob");
    const base64 = await blobToBase64(blob);
    await apiFetch<{ key: string }>("/documents/r2-upload", {
      method: "POST",
      body: JSON.stringify({ key: existingKey, contentType: "application/pdf", base64 }),
    });
    return existingKey;
  }
  // dateIST() formats in Asia/Kolkata so the R2 folder key matches the IST
  // calendar month (toISOString().slice(0,7) returns the UTC month, which is
  // the previous month after 18:30 UTC).
  const ym = dateIST().slice(0, 7);
  const safeName = fileName.replace(/[^a-zA-Z0-9.\-_]/g, "_");
  // patientId sits between the category and the date so a per-patient listing
  // is a single R2 list-prefix (`certificates/{patientId}/`). An empty
  // patientId (rare — only certificates allow it) uses `unsorted` so the key
  // still passes the /documents/r2-upload prefix allowlist.
  const pid = patientId && patientId.length > 0 ? patientId : "unsorted";
  const ctx = visitId ? `${pid}/${visitId}` : pid;
  const key = `${category}/${ctx}/${ym}/${crypto.randomUUID()}-${safeName}.pdf`;

  const blob = doc.output("blob");
  const base64 = await blobToBase64(blob);

  await apiFetch<{ key: string }>("/documents/r2-upload", {
    method: "POST",
    body: JSON.stringify({ key, contentType: "application/pdf", base64 }),
  });
  return key;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip the "data:application/pdf;base64," prefix
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
